/**
 * Zugriff auf die Verwaltungs-API.
 *
 * Eine Stelle für alle Anfragen: Anmeldung, Fehlerbehandlung und die
 * Übersetzung technischer Fehler in Sätze, die eine Sachbearbeiterin
 * versteht. Ein "500 Internal Server Error" hilft niemandem weiter.
 */

const BASIS = import.meta.env.VITE_API ?? '';

/** Wird gesetzt, wenn die Anmeldung fehlschlägt. */
let beiAbmeldung = () => {};

/**
 * @param {() => void} rueckruf
 */
export function aufAbmeldungReagieren(rueckruf) {
  beiAbmeldung = rueckruf;
}

/**
 * @returns {string}
 */
export function tokenLesen() {
  return sessionStorage.getItem('zugangstoken') ?? '';
}

/**
 * @param {string} token
 */
export function tokenSetzen(token) {
  sessionStorage.setItem('zugangstoken', token);
}

export function tokenLoeschen() {
  sessionStorage.removeItem('zugangstoken');
}

/**
 * Übersetzt technische Meldungen in Sätze, mit denen eine Sachbearbeiterin
 * etwas anfangen kann.
 *
 * "connect ECONNREFUSED 127.0.0.1:32526" sagt einer Administratorin alles und
 * einer Sachbearbeiterin nichts. Der technische Wortlaut geht nicht verloren -
 * er wandert in den Aufklappbereich "Technische Angaben".
 *
 * @param {string} roh
 * @returns {string}
 */
export function meldungVerstaendlich(roh) {
  if (!roh) return 'Der Zustand ist unbekannt.';

  if (/ECONNREFUSED/i.test(roh)) {
    return 'Der Mailserver nimmt keine Verbindung an. Bitte prüfen, ob die Angaben zum Versandweg stimmen und der Dienst erreichbar ist.';
  }
  if (/ETIMEDOUT|ESOCKETTIMEDOUT/i.test(roh)) {
    return 'Der Mailserver antwortet nicht. Möglicherweise blockiert eine Firewall die Verbindung.';
  }
  if (/ENOTFOUND|EAI_AGAIN/i.test(roh)) {
    return 'Die Adresse des Mailservers lässt sich nicht auflösen. Bitte den Servernamen prüfen.';
  }
  if (/EAUTH|535|authentication/i.test(roh)) {
    return 'Der Mailserver hat die Anmeldedaten abgelehnt. Bitte Benutzername und Kennwort prüfen.';
  }
  if (/certificate|CERT_|SSL|TLS/i.test(roh)) {
    return 'Die verschlüsselte Verbindung zum Mailserver kam nicht zustande. Bitte die Verschlüsselungseinstellungen prüfen.';
  }
  if (/Mail\.Send|Administratorzustimmung|consent/i.test(roh)) {
    return 'Microsoft 365 verweigert den Zugriff auf das Absenderpostfach. In der Azure-Anwendung fehlt vermutlich die Berechtigung „Mail.Send" mit Administratorzustimmung.';
  }
  if (/Startkontingent|Kontingent/i.test(roh)) return roh;

  // Unbekannte technische Meldungen bleiben stehen, aber gekürzt.
  return roh.length > 160 ? `${roh.slice(0, 157)}…` : roh;
}

/** Fehler mit einer für Menschen lesbaren Meldung. */
export class ApiFehler extends Error {
  /**
   * @param {string} nachricht
   * @param {object} [zusatz]
   * @param {number} [zusatz.status]
   * @param {unknown} [zusatz.details]
   */
  constructor(nachricht, zusatz = {}) {
    super(nachricht);
    this.name = 'ApiFehler';
    this.status = zusatz.status;
    this.details = zusatz.details;
  }
}

/**
 * @param {string} pfad
 * @param {{ methode?: string, koerper?: unknown }} [optionen]
 * @returns {Promise<any>}
 */
export async function api(pfad, optionen = {}) {
  let antwort;
  try {
    antwort = await fetch(`${BASIS}${pfad}`, {
      method: optionen.methode ?? 'GET',
      headers: {
        'content-type': 'application/json',
        authorization: `Bearer ${tokenLesen()}`,
      },
      ...(optionen.koerper !== undefined ? { body: JSON.stringify(optionen.koerper) } : {}),
    });
  } catch {
    // Netzwerkfehler kommen ohne Antwort - der Server ist nicht erreichbar.
    throw new ApiFehler(
      'Der Server ist nicht erreichbar. Läuft er, und stimmt die Adresse?',
    );
  }

  if (antwort.status === 401) {
    tokenLoeschen();
    beiAbmeldung();
    throw new ApiFehler('Die Anmeldung ist abgelaufen. Bitte erneut anmelden.');
  }

  if (antwort.status === 204) return null;

  const text = await antwort.text();
  /** @type {any} */
  let daten = null;
  try {
    daten = text ? JSON.parse(text) : null;
  } catch {
    daten = null;
  }

  if (!antwort.ok) {
    throw new ApiFehler(daten?.fehler ?? `Die Anfrage ist fehlgeschlagen (${antwort.status}).`, {
      status: antwort.status,
      details: daten?.befunde ?? daten?.details,
    });
  }
  return daten;
}

// --- Kurzwege für die einzelnen Bereiche ---------------------------------

export const Status = {
  /** @returns {Promise<any>} */
  laden: () => api('/api/status'),
  /**
   * Prüfung der Absenderdomain im DNS (SPF, DKIM, DMARC). Braucht ein bis
   * zwei Sekunden — deshalb getrennt vom übrigen Status.
   *
   * @returns {Promise<any>}
   */
  absender: () => api('/api/pruefung/absender'),
};

export const Kontakte = {
  /**
   * @param {{ suche?: string, limit?: number, offset?: number }} [filter]
   */
  liste: (filter = {}) => {
    const p = new URLSearchParams();
    if (filter.suche) p.set('suche', filter.suche);
    if (filter.limit) p.set('limit', String(filter.limit));
    if (filter.offset) p.set('offset', String(filter.offset));
    const abfrage = p.toString();
    return api(`/api/kontakte${abfrage ? `?${abfrage}` : ''}`);
  },
  /**
   * @param {{ csv: string, quelle: string, einwilligungsQuelle?: string }} eingabe
   */
  importieren: (eingabe) => api('/api/kontakte/import', { methode: 'POST', koerper: eingabe }),
  /**
   * Legt einen einzelnen Empfänger an — für den Mandanten, der beim
   * Erstgespräch um den Newsletter bittet, ohne dass dafür eine CSV-Datei
   * gebaut werden muss.
   *
   * @param {{ email: string, anredeForm?: string, vorname?: string, nachname?: string, titelVor?: string, titelNach?: string, firma?: string, einwilligungsQuelle?: string }} eingabe
   */
  anlegen: (eingabe) => api('/api/kontakte', { methode: 'POST', koerper: eingabe }),
  /**
   * Eine bereits dokumentierte Einwilligung wird dabei NICHT überschrieben.
   *
   * @param {number} id
   * @param {{ email: string, anredeForm?: string, vorname?: string, nachname?: string, titelVor?: string, titelNach?: string, firma?: string, einwilligungsQuelle?: string }} eingabe
   */
  aktualisieren: (id, eingabe) => api(`/api/kontakte/${id}`, { methode: 'PUT', koerper: eingabe }),
  /**
   * Die Adresse bleibt danach gesperrt, damit ein späterer Listenimport sie
   * nicht erneut anschreibt.
   *
   * @param {number} id
   */
  loeschen: (id) => api(`/api/kontakte/${id}`, { methode: 'DELETE' }),
  /** @param {number} id */
  sperren: (id) => api(`/api/kontakte/${id}/sperren`, { methode: 'POST' }),
  /** @param {number} id */
  entsperren: (id) => api(`/api/kontakte/${id}/entsperren`, { methode: 'POST' }),
};

export const Segmente = {
  liste: () => api('/api/segmente'),
  /**
   * @param {{ name: string, beschreibung?: string, kontaktIds?: number[] }} eingabe
   */
  anlegen: (eingabe) => api('/api/segmente', { methode: 'POST', koerper: eingabe }),
};

export const Vorlagen = {
  liste: () => api('/api/vorlagen'),
  /** Fertige Vorlagen zum Loslegen. */
  galerie: () => api('/api/galerie'),
  /** @param {number} id */
  laden: (id) => api(`/api/vorlagen/${id}`),
  /**
   * @param {{ name: string, mjml: string, betreff?: string, vorschautext?: string, entwurfJson?: string }} eingabe
   */
  anlegen: (eingabe) => api('/api/vorlagen', { methode: 'POST', koerper: eingabe }),
  /**
   * @param {number} id
   * @param {{ name: string, mjml: string, betreff?: string, vorschautext?: string, entwurfJson?: string }} eingabe
   */
  speichern: (id, eingabe) => api(`/api/vorlagen/${id}`, { methode: 'PUT', koerper: eingabe }),
  /** Echte Kontakte für die Vorschau — bewusst eine Mischung, keine Auswahl der ersten. */
  vorschauKontakte: () => api('/api/vorschau/kontakte'),
  /**
   * Als Kopie anlegen — der häufigste Fall: das letzte Rundschreiben als
   * Ausgangspunkt für das nächste.
   * @param {number} id
   */
  duplizieren: (id) => api(`/api/vorlagen/${id}/duplizieren`, { methode: 'POST' }),
  /**
   * Löschen. Schlägt mit 409 fehl, wenn eine Aussendung die Vorlage verwendet —
   * sie bleibt dann erhalten, damit nachvollziehbar bleibt, was versendet wurde.
   * @param {number} id
   */
  loeschen: (id) => api(`/api/vorlagen/${id}`, { methode: 'DELETE' }),
  /**
   * @param {{ mjml: string, musterKontakt?: object }} eingabe
   */
  vorschau: (eingabe) => api('/api/vorschau', { methode: 'POST', koerper: eingabe }),
  /**
   * Prüfung gegen die Außenwelt: ruft jede Bildadresse und jeden Verweis
   * tatsächlich ab. Dauert Sekunden — deshalb getrennt von der Vorschau.
   *
   * @param {{ mjml: string, betreff?: string }} eingabe
   */
  zustellung: (eingabe) => api('/api/pruefung/zustellung', { methode: 'POST', koerper: eingabe }),
};

export const Kampagnen = {
  liste: () => api('/api/kampagnen'),
  /**
   * @param {{ name: string, vorlageId: number, segmentId?: number | null, betreff: string }} eingabe
   */
  anlegen: (eingabe) => api('/api/kampagnen', { methode: 'POST', koerper: eingabe }),
  /**
   * Eine einzelne Aussendung mit Empfängerstand (auch "unklar") und
   * Zeitpunkten - für die aufklappbare Detailansicht in der Liste.
   *
   * @param {number} id
   */
  holen: (id) => api(`/api/kampagnen/${id}`),
  /**
   * Löscht einen Entwurf. Sobald etwas versendet wurde, lehnt der Server mit
   * 409 ab - eine versendete Aussendung ist der Nachweis, wer wann was
   * bekommen hat.
   *
   * @param {number} id
   */
  loeschen: (id) => api(`/api/kampagnen/${id}`, { methode: 'DELETE' }),
  /** @param {number} id */
  vorschau: (id) => api(`/api/kampagnen/${id}/vorschau`),
  /**
   * Probeversand. Eine Adresse oder mehrere — mehrere sind der sinnvolle Fall,
   * weil sich erst im Vergleich zeigt, ob eine Aussendung bei allen großen
   * Anbietern im Posteingang landet.
   *
   * @param {number} id
   * @param {string|string[]} an
   */
  test: (id, an) => api(`/api/kampagnen/${id}/test`, { methode: 'POST', koerper: { an } }),
  /**
   * Startet den Versand. Ohne Angabe geht es an alle Empfänger; mit
   * `hoechstens` nur an eine erste Teilmenge (gestaffelter Start).
   *
   * @param {number} id
   * @param {{ hoechstens?: number }} [optionen]
   */
  senden: (id, optionen = {}) =>
    api(`/api/kampagnen/${id}/senden`, {
      methode: 'POST',
      ...(optionen.hoechstens ? { koerper: { hoechstens: optionen.hoechstens } } : {}),
    }),
  /** @param {number} id */
  anhalten: (id) => api(`/api/kampagnen/${id}/anhalten`, { methode: 'POST' }),
  /**
   * Kennzahlen einer Aussendung: Beschwerde- und Unzustellbarquote gegen die
   * Schwellen, an denen Google/Yahoo abstufen.
   *
   * @param {number} id
   */
  kennzahlen: (id) => api(`/api/kampagnen/${id}/kennzahlen`),
};

export const Ruecklauf = {
  /**
   * Holt Rückmeldungen aus dem Absenderpostfach (Unzustellbarkeit,
   * Beschwerden) und pflegt die Sperrliste. Antwortet mit 503, wenn der
   * Abruf nicht eingerichtet ist — die Fehlermeldung erklärt dann, was in
   * der .env fehlt.
   */
  abrufen: () => api('/api/ruecklauf/abrufen', { methode: 'POST' }),
};

/** Bausteinsammlung der Kanzlei: gebaute Abschnitte und Textstellen. */
export const Bausteine = {
  /** @param {'abschnitt'|'text'} [art] */
  liste: (art) => api(`/api/bausteine${art ? `?art=${art}` : ''}`),
  /** @param {{ art: 'abschnitt'|'text', name: string, inhalt: string }} eingabe */
  anlegen: (eingabe) => api('/api/bausteine', { methode: 'POST', koerper: eingabe }),
  /** @param {number} id */
  loeschen: (id) => api(`/api/bausteine/${id}`, { methode: 'DELETE' }),
};

export const KI = {
  /**
   * @param {{ thema: string, zielgruppe?: string, tonfall?: string, abschnitte?: number }} eingabe
   */
  entwurf: (eingabe) => api('/api/ki/entwurf', { methode: 'POST', koerper: eingabe }),
};
